import type { SubmitHandler } from 'react-hook-form';
import { useUntrustedData } from '@auth0/auth0-acul-react/signup-id';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import Input from 'creative-ui/uikit/Input';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Captcha from '@/components/Captcha';
import { useCaptcha } from '@/components/Captcha/useCaptcha';
import { Flex } from '@/components/Layout/Flex/Flex';
import { Text } from '@/components/Typography/Text/Text';
import { useSignupIdManager } from '@/screens/signup-id/hooks/useSignupIdManager';

interface EmailSignupFormProps {
  fieldErrors: Record<string, string>;
}

type FormValues = {
  email: string;
  captcha: string;
};

export function EmailSignupForm({
  fieldErrors,
}: Readonly<EmailSignupFormProps>) {
  const { t } = useTranslation(['signupId', 'common']);

  const { handleSignup, loginLink, isCaptchaAvailable, captcha } =
    useSignupIdManager();
  const { captchaConfig, captchaProps, captchaValue } = useCaptcha(
    captcha || undefined,
  );

  const untrustedData = useUntrustedData();
  const returnedUsername = String(
    untrustedData?.submittedFormData?.username ||
      untrustedData?.submittedFormData?.email ||
      '',
  );

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      email: returnedUsername,
      captcha: '',
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      await handleSignup({
        email: data.email,
        username: data.email,
        captcha: isCaptchaAvailable && captchaValue ? captchaValue : undefined,
      });
    } catch {
      setError('root', {
        type: 'manual',
        message: t('errorGeneric'),
      });
    }
  };

  const handleLoginClick = () => {
    // Redirect to login page
    if (loginLink) {
      window.location.assign(loginLink);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gapY="16px">
        <Controller
          name="email"
          control={control}
          rules={{
            required: t('errorRequired'),
          }}
          render={({ field }) => (
            <Input
              type="email"
              {...field}
              placeholder={t('emailPlaceholder')}
              status={
                (errors.email?.message ??
                fieldErrors.email ??
                fieldErrors.username)
                  ? 'error'
                  : undefined
              }
              statusMessage={
                errors.email?.message ??
                fieldErrors.email ??
                fieldErrors.username
              }>
              {t('email')}
            </Input>
          )}
        />

        {isCaptchaAvailable && captchaConfig && (
          <Captcha
            captcha={captchaConfig}
            name="captcha"
            control={control}
            setError={setError}
            clearErrors={clearErrors}
            error={fieldErrors.captcha}
            rules={{ required: t('common:captcha.errorRequired') }}
            {...captchaProps}
          />
        )}

        <CtaContainer stack>
          <Cta
            className="c-submitCta"
            type="primary"
            submit
            disabled={isSubmitting}
            isWaiting={isSubmitting}>
            {t('continue')}
          </Cta>
        </CtaContainer>

        <Flex justify="center" align="center">
          <Text align="center">
            {t('alreadyHaveAccount')}{' '}
            <Cta type="ghost-inline" onClick={handleLoginClick}>
              {t('login')}
            </Cta>
          </Text>
        </Flex>
      </Flex>
    </form>
  );
}
